import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAd } from '../context/AdContext';
import { safeAlert } from '../utils/gameMonetize';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiClock } = FiIcons;

const AdManager = ({ showAdButton = true, position = 'bottom-right' }) => {
  const { canWatchAd, calculateReward, completeAd } = useAd();
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  // Force re-render for timer
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const handleWatchAd = async () => {
    // FIX: Using 'rewardedVideo' to match config keys in AdContext
    const rewardAmount = calculateReward('rewardedVideo');
    const can = canWatchAd('rewardedVideo');
    
    if (!can.canWatch) {
      safeAlert(
        can.reason === 'cooldown' 
          ? `Please wait ${formatTimeLeft(can.timeLeft)}` 
          : 'Daily limit reached'
      );
      return;
    }

    setLoading(true);
    
    completeAd('rewardedVideo', 'user123')
      .then(() => {
        console.log("Ad process finished");
      })
      .catch((err) => {
        console.warn("Ad error:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatTimeLeft = (milliseconds) => {
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getCooldownTime = () => {
    const cooldown = canWatchAd('rewardedVideo');
    return cooldown.timeLeft || 0;
  };

  const AdButton = () => (
    <motion.div 
      className={`fixed ${position === 'bottom-right' ? 'bottom-24 right-4' : 'bottom-24 left-4'} z-40`}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
    >
      <div className="relative">
        {canWatchAd('rewardedVideo').canWatch && !loading && (
          <motion.div 
            className="absolute inset-0 bg-green-500 rounded-full"
            animate={{ scale: [1, 1.2, 1], opacity: [0.7, 0.3, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        
        <motion.button
          onClick={handleWatchAd}
          disabled={!canWatchAd('rewardedVideo').canWatch || loading}
          className={`relative px-4 py-3 rounded-full font-bold transition-all ${
            canWatchAd('rewardedVideo').canWatch && !loading
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
          whileHover={{ scale: canWatchAd('rewardedVideo').canWatch && !loading ? 1.05 : 1 }}
          whileTap={{ scale: 0.95 }}
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <span className="text-sm">Loading...</span>
            </div>
          ) : getCooldownTime() > 0 ? (
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiClock} className="w-4 h-4" />
              <span className="text-sm font-mono">{formatTimeLeft(getCooldownTime())}</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiPlay} className="w-4 h-4" />
              <span className="text-sm">Watch Ad</span>
            </div>
          )}
        </motion.button>

        {canWatchAd('rewardedVideo').canWatch && !loading && (
          <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs px-2 py-1 rounded-full font-bold">
            +{calculateReward('rewardedVideo')}
          </div>
        )}
      </div>
    </motion.div>
  );

  return showAdButton ? <AdButton /> : null;
};

export default AdManager;